import React from 'react';
import {useForm} from 'react-hook-form';
import {Keyboard, Platform, Pressable, StyleSheet, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';

import {Icon} from '../../components/Icon';
import Button from '../../components/ui-controls/Button';
import TextInput from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {AccountScreenAction, RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventScreenName} from '../../utils/analytics/events';
import {logError} from '../../utils/errorLogging';

import {AccountEntryFormData, accountEntrySchema} from './schemas/authSchemas';

const AccountEntryScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_NOT_LOGGED_IN>) => {
  const {userStore} = useStores();

  const [isSubmitting, setSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  const {
    handleSubmit,
    formState: {errors, touchedFields},
    reset,
    setValue,
    watch,
  } = useForm<AccountEntryFormData>({
    resolver: zodResolver(accountEntrySchema),
    defaultValues: {email: ''},
    mode: 'onTouched',
  });

  const emailValue = watch('email');

  const onSubmit = async (formValues: AccountEntryFormData) => {
    setSubmitting(true);
    setApiError(null);

    try {
      const validationResponse = await apiService.validateEmail(formValues.email);

      if (!validationResponse.data.valid) {
        setApiError('Please enter a valid email');
        setSubmitting(false);
        return;
      }

      const data = await userStore.checkEmailNew(formValues.email);

      switch (data?.status) {
        case 'ACCOUNT_LOGIN':
          navigation.navigate(AppRoute.ACCOUNT_LOGIN, {email: formValues.email});
          break;
        case 'REWARD_FULL_SIGNUP':
          navigation.navigate(AppRoute.ROOT_REWARDS_FLOW, {
            screen: AppRoute.REWARDS_FULL_SIGN_UP,
            params: {firstName: data.first_name},
          });
          break;
        case 'ACCOUNT_CREATE':
          navigation.navigate(AppRoute.ACCOUNT_CREATE, {
            email: formValues.email,
            action: AccountScreenAction.CREATE,
          });
          break;
        case 'ERROR':
        default:
          setApiError(data?.message || 'An error occurred. Please try again.');
          break;
      }
    } catch (error) {
      logError(error, 'AccountEntryScreen.validateEmail');
      setApiError('Unable to validate email. Please try again.');
    }

    setSubmitting(false);
  };

  // DIS-474: Reset form state when screen gains focus to prevent stale state
  // persisting between visits and breaking automation tests
  useFocusEffect(
    React.useCallback(() => {
      reset({email: ''});
      setApiError(null);
      setSubmitting(false);
    }, [reset]),
  );

  return (
    <ContentView style={styles.main} testID="account-entry-screen">
      <View style={styles.inputForm}>
        <TextInput
          testID="login-email-input"
          value={emailValue}
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect={false}
          autoFocus={true}
          errorMessageWhenTouched={errors.email?.message}
          keyboardType="email-address"
          label="Email Address"
          leftIcon={<Icon name="envelope" iconStyle="regular" />}
          onBlur={() => setValue('email', emailValue, {shouldTouch: true, shouldValidate: true})}
          onChangeText={(text: string) => setValue('email', text, {shouldValidate: true})}
          onSubmitEditing={handleSubmit(onSubmit)}
          placeholder="Enter your email"
          returnKeyLabel="next"
          returnKeyType="next"
          touched={!!touchedFields.email}
        />
        {!!apiError && <Text style={styles.errorMessage}>{apiError}</Text>}
      </View>
      <Button
        testID="login-submit-button"
        containerStyle={{alignSelf: 'center'}}
        onPress={handleSubmit(onSubmit)}
        title="Next"
        disabled={isSubmitting}
        loading={isSubmitting}
      />
      <View style={styles.resetOuter}>
        {emailValue && !errors.email ? (
          <Pressable
            style={{alignSelf: 'center'}}
            onPress={() => {
              if (Platform.OS === 'android') Keyboard.dismiss();
              userStore.initiatePasswordReset(emailValue, () => navigation.navigate(AppRoute.HOME));

              Analytics.logEvent({
                eventName: EventName.PasswordResetRequest,
                parameters: {
                  screen_name: EventScreenName.AccountEntry,
                },
              });
            }}>
            <Text style={styles.resetLink}>Request password reset</Text>
          </Pressable>
        ) : (
          <Text style={styles.resetText}>Need a password reset? Enter your account email.</Text>
        )}
      </View>
    </ContentView>
  );
};

export default AccountEntryScreen;

const styles = StyleSheet.create({
  main: {
    flex: 1,
    alignItems: 'stretch',
    ...Platform.select({
      native: {
        justifyContent: 'flex-start',
      },
      web: {
        justifyContent: 'center',
      },
    }),
  },
  inputForm: {
    marginBottom: 16,
  },
  validationError: {
    fontSize: 16,
  },
  errorMessage: {
    color: theme.colour.text.error,
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
  resetOuter: {
    alignItems: 'center',
    marginTop: 16,
  },
  resetLink: {
    color: theme.colour.text.heading.primary,
    fontSize: 14,
  },
  resetText: {
    color: theme.colour.text.content.primary,
    fontSize: 12,
  },
});
